import {Controller} from "@hotwired/stimulus"
import Cropper from "cropperjs";

// Connects to data-controller="image-cropper"
export default class extends Controller {
  static targets = ["remove", "imagePreview", "uploadField", "imageUploadArea", "removeImgField", "modal",
    "cropperImg"]

  static values = {
    circular: Boolean,
  }

  connect() {
    this.cropper = null;
    this.allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/jpg", "image/bmp", "image/svg+xml"]
  }

  triggerImageInput() {
    this.uploadFieldTarget.click();
  }

  previewImage() {
    if (this.hasRemoveImgFieldTarget) this.removeImgFieldTarget.value = false
    const input = event.target
    if (!input.files && !input.files[0]) return

    const file = input.files[0];
    if (!this.allowedTypes.includes(file.type)) {
      input.value = ""
      alert(this.getErrorMessage(file.type))
      return
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this.cropperImgTarget.src = e.target.result;
      this.openModal();
    };
    reader.readAsDataURL(file);
  }

  getErrorMessage(fileType) {
    return `Unsupported file type: ${fileType}. Please upload a valid image file (${this.allowedTypes.join(", ")}).`
  }


  // Resolve the crop modal WITHIN this controller's own scope. Multiple croppers
  // can live on one page (e.g. logo + cover photo on the org edit form); a global
  // getElementById("cropImgModal") would always return the first modal, so every
  // cropper would crop into the same (wrong) modal. querySelector on this.element
  // keeps each cropper bound to its own modal.
  modalEl() {
    if (this.hasModalTarget) return this.modalTarget;
    // Each cropper renders exactly one .modal inside its own element; scope the
    // lookup to this.element so two croppers on one page stay independent.
    return this.element.querySelector(".modal") || document.getElementById("cropImgModal");
  }

  openModal() {
    const modal = this.modalEl();
    $(modal).modal('show');

    // Wait for the modal to be fully shown before initializing Cropper
    $(modal).on('shown.bs.modal', () => {
      if (this.cropper) {
        this.cropper.destroy();
      }
      if (this.circularValue) {
        this.cropper = new Cropper(this.cropperImgTarget, {
          aspectRatio: 1, // 1:1 for square (circle-friendly)
          viewMode: 1,
          autoCropArea: 0.8,
          responsive: true,
          background: false,
          movable: true,
          zoomable: true,
          rotatable: true,
          scalable: true,
          cropBoxMovable: true,
          cropBoxResizable: true,
          ready: () => {
            modal.classList.add('circular-cropper-wrapper');
          }
        });
      } else {
        this.cropper = new Cropper(this.cropperImgTarget, {
          aspectRatio: 2,
          viewMode: 1,
          autoCropArea: 0.8,
          responsive: true,
          movable: true,
          zoomable: true,
          rotatable: true,
          scalable: true,
          cropBoxMovable: true,
          cropBoxResizable: true,
        });
      }

    });

    this.clearCropper(modal)
  }

  clearCropper(modal) {
    // Clean up event listener when modal is hidden
    $(modal).on('hidden.bs.modal', () => {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      $(modal).off('shown.bs.modal');
      $(modal).off('hidden.bs.modal');
    });
  }

  closeModal() {
    $(this.modalEl()).modal('hide')
  }

  removeImage() {
    // Clear the file input
    if (this.hasRemoveImgFieldTarget) {
      this.removeImgFieldTarget.value = true
    }

    // Hide the image preview and remove icon
    this.imagePreviewTarget.classList.add("d-none")
    this.removeTarget.classList.add("d-none")

    // Show the upload area again
    this.imageUploadAreaTarget.classList.remove("d-none")
  }

  showImage() {
    this.imagePreviewTarget.style.display = "block"
    this.imagePreviewTarget.classList.remove("d-none")
    this.removeTarget.classList.remove("d-none") // Show the remove icon
    this.imageUploadAreaTarget.classList.add("d-none") // Hide the upload area
  }

  cropImage() {
    this.circularValue ? this.cropImageCircular() : this.cropImageRegular()
  }

  cropImageRegular() {
    if (this.cropper) {
      const canvas = this.cropper.getCroppedCanvas();

      canvas.toBlob((blob) => {
        const croppedFile = new File([blob], "cropped-image.png", {
          type: "image/png",
        });

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(croppedFile);

        this.uploadFieldTarget.files = dataTransfer.files;

        const croppedImageURL = URL.createObjectURL(blob);
        this.imagePreviewTarget.src = croppedImageURL

        this.closeModal();
        this.showImage();
      }, "image/png");
    }
  }

  cropImageCircular() {
    if (this.cropper) {
      const canvas = this.cropper.getCroppedCanvas();

      const size = canvas.width; // assuming square crop
      const circularCanvas = document.createElement("canvas");
      circularCanvas.width = size;
      circularCanvas.height = size;
      const ctx = circularCanvas.getContext("2d");

      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2, true);
      ctx.closePath();
      ctx.clip();

      ctx.drawImage(canvas, 0, 0);

      circularCanvas.toBlob((blob) => {
        const croppedFile = new File([blob], "circular-image.png", {
          type: "image/png",
        });

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(croppedFile);
        this.uploadFieldTarget.files = dataTransfer.files;

        const croppedImageURL = URL.createObjectURL(blob);
        this.imagePreviewTarget.src = croppedImageURL

        this.closeModal();
        this.showImage();
      }, "image/png");
    }
  }
}
